import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { cn } from "@arcadeai/design-system/lib/utils";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { type Control, useFormContext } from "react-hook-form";
import { useIssuerCheckQuery } from "../../api/hooks";
import type { UserSourceCreateRequest } from "../../types";

type IssuerFormFieldProps = {
  control: Control<UserSourceCreateRequest>;
};

export function IssuerFormField({ control }: IssuerFormFieldProps) {
  const { setError, clearErrors } = useFormContext<UserSourceCreateRequest>();

  // The issuer to check, committed on blur. Holding it here keeps the check
  // self-contained: it resets when the field unmounts (the sheet closes), so no
  // parent has to clear it.
  const [committedIssuer, setCommittedIssuer] = useState("");
  const { data, isFetching } = useIssuerCheckQuery(committedIssuer);

  // Surface a rejected pre-check through the field's own error channel — the
  // same one zod uses — so "invalid" has a single style: the field reddens via
  // aria-invalid and the reason renders in FormMessage. Track ownership so
  // clearing our message can't wipe a zod error (required, too long) we didn't set.
  const ownsError = useRef(false);
  useEffect(() => {
    if (data?.ok === false) {
      setError("issuer", { type: "manual", message: data.message });
      ownsError.current = true;
    } else if (ownsError.current) {
      clearErrors("issuer");
      ownsError.current = false;
    }
  }, [data, setError, clearErrors]);

  return (
    <FormField
      control={control}
      name="issuer"
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            Issuer URL
            <span className="text-destructive"> *</span>
          </FormLabel>
          <div className="relative">
            <FormControl>
              <Input
                {...field}
                className={cn("pr-9", data?.ok === true && "border-success")}
                onBlur={(e) => {
                  const trimmed = e.currentTarget.value.trim();
                  // Persist the trimmed value so the field shows exactly what
                  // gets validated, then run the check.
                  if (trimmed !== e.currentTarget.value) {
                    field.onChange(trimmed);
                  }
                  field.onBlur();
                  setCommittedIssuer(trimmed);
                }}
                onChange={(e) => {
                  field.onChange(e);
                  setCommittedIssuer("");
                }}
                type="url"
              />
            </FormControl>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              {isFetching && (
                <Loader2
                  aria-label="Checking issuer"
                  className="size-4 animate-spin text-muted-foreground"
                />
              )}
              {!isFetching && data?.ok === true && (
                <CheckCircle2
                  aria-label="Issuer reachable"
                  className="size-4 text-success"
                />
              )}
            </div>
          </div>
          <FormDescription>
            OIDC issuer URL — e.g. https://accounts.example.com
          </FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
